// @flow
import { LANGUAGES } from "@utils/Constants";

/**
 * Filename normalization utilities for SalesHub
 *
 * CRITICAL: This implementation MUST be kept in sync across all SalesHub repositories:
 * - saleshub-frontend/src/utils/normalization/index.js (this file)
 * - saleshub-backend/src/utils/normalization/index.js
 * - saleshub-public/src/utils/normalization/index.js
 * - saleshub-onedrivesync/src/utils/normalization/index.js
 *
 * Any changes to normalization logic MUST be applied to ALL repositories.
 */

/**
 * Generate a normalized filename with optional language code
 * PRESERVES file extensions (e.g., .pdf, .docx, .xlsx)
 *
 * @param {string} fileName - The original filename or title to normalize
 * @param {string} language - Optional language value (e.g., 'English', 'Nederlands', 'Français')
 * @returns {string} Normalized filename with extension preserved
 *
 * @example
 * generateNormalizedFileName("Café René.pdf", "Français") // Returns: "Cafe_Rene_fr.pdf"
 * generateNormalizedFileName("Strategy.docx") // Returns: "Strategy.docx"
 */
export const generateNormalizedFileName = (
    fileName: string,
    language?: string,
) => {
    // Extract file extension if present
    const lastDotIndex = fileName.lastIndexOf(".");
    let nameWithoutExt = fileName;
    let extension = "";

    if (lastDotIndex > 0) {
        nameWithoutExt = fileName.substring(0, lastDotIndex);
        extension = fileName.substring(lastDotIndex); // includes the dot
    }

    // Normalize the filename without extension
    let normalizedName = nameWithoutExt;

    // Replace spaces, dashes, and periods with underscores
    normalizedName = normalizedName.replace(/[\s\-.]+/g, "_");

    // Unicode normalization: decompose accented characters and remove diacritics
    // This handles ALL accented characters (é, è, ê, ë, à, ö, ü, ñ, etc.)
    normalizedName = normalizedName
        .normalize("NFD") // Decompose: é → e + combining accent
        .replace(/[\u0300-\u036f]/g, ""); // Remove all combining diacritical marks

    // Remove all remaining special characters (but keep underscores and alphanumeric)
    normalizedName = normalizedName.replace(/[^\w]/gi, "_");

    // Replace multiple consecutive underscores with single underscore
    normalizedName = normalizedName.replace(/_+/g, "_");

    // Remove leading/trailing underscores
    normalizedName = normalizedName.replace(/^_+|_+$/g, "");

    // Add language code if provided
    if (language) {
        normalizedName = `${normalizedName}_${getIsoLanguage(language)}`;
    }

    // Append the original extension back
    return normalizedName + extension;
};

export const getIsoLanguage = (language: string) => {
    return (
        LANGUAGES.find(lang => lang.value === language)?.key ||
        LANGUAGES[0]?.key ||
        "en"
    );
};
